import React, { useEffect, useState } from "react"
import { Grid, Typography } from "@material-ui/core"

import { ChatBotFlowBlockContent } from "@/protocols/chatBot"
import { CustomFieldGroupList } from "@/protocols/customField"

import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"

import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import { SparklesIcon } from "@/assets/icons"

import CustomFieldMultiSelect, { CustomFieldOption } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/CustomFieldFillBlock/CustomFieldMultiSelect"
import CustomFieldCard from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/CustomFieldFillBlock/CustomFieldCard"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/CustomFieldFillBlock/CustomFieldSelectionEditor/styles"

const AI_NOTE_PARAGRAPH = "A IA lê as mensagens do último protocolo de atendimento e preenche automaticamente os campos personalizados que reconhecer entre os adicionados acima. Pode preencher mais de um campo na mesma execução."
const DRAWER_WIDTH = "540px"

type FieldConfig = {
	customFieldId: number
	variable: string
	instruction: string
	conflictRule: "overwrite" | "keep"
}

type CustomFieldSelectionEditorProps = {
	chatBotFlowBlockContent: ChatBotFlowBlockContent
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
}

const buildOptionsFromGroups = (groups: CustomFieldGroupList[]): CustomFieldOption[] => {
	return groups.flatMap(group =>
		group.custom_field_values.map(field => ({
			id: field.id,
			name: field.display_name,
			displayName: field.display_name,
			variable: field.variable,
			groupId: group.id,
			groupDisplayName: group.display_name
		}))
	)
}

const buildInitialFieldConfigs = (content: ChatBotFlowBlockContent): FieldConfig[] => {
	return content?.customFieldFillData?.fields ?? []
}

const buildFields = (options: CustomFieldOption[], configs: Record<number, FieldConfig>): FieldConfig[] => {
	return options.map(option => {
		const config = configs[option.id]

		return {
			customFieldId: option.id,
			variable: option.variable,
			instruction: config?.instruction ?? "",
			conflictRule: config?.conflictRule ?? "overwrite"
		}
	})
}

const CustomFieldSelectionEditor: React.FC<CustomFieldSelectionEditorProps> = (props) => {
	const {
		chatBotFlowBlockContent,
		onSave,
		children
	} = props

	const contentEditor = useContentEditor({ chatBotFlowBlockContent })

	const [customFieldGroups, setCustomFieldGroups] = useState<CustomFieldGroupList[]>([])
	const [selectedOptions, setSelectedOptions] = useState<CustomFieldOption[]>([])
	const [fieldConfigs, setFieldConfigs] = useState<Record<number, FieldConfig>>({})

	const classes = useStyles()

	const allOptions = buildOptionsFromGroups(customFieldGroups)

	useEffect(() => {
		const loadCustomFieldGroups = async () => {
			try {
				const { data } = await ApiService.get("/custom-fields/group")
				setCustomFieldGroups(data.customFieldsGroups)
			} catch (error) {
				ErrorHandlerService.handle(error as ErrorType)
			}
		}

		loadCustomFieldGroups()
	}, [])

	const handleOpen = () => {
		contentEditor.reset()

		const savedFieldConfigs = buildInitialFieldConfigs(chatBotFlowBlockContent)
		const configsByFieldId: Record<number, FieldConfig> = {}
		const savedOptions: CustomFieldOption[] = []

		savedFieldConfigs.forEach(config => {
			configsByFieldId[config.customFieldId] = config

			const matchingOption = allOptions.find(option => option.id === config.customFieldId)
			if (matchingOption) {
				savedOptions.push(matchingOption)
			} else {
				savedOptions.push({
					id: config.customFieldId,
					name: config.variable,
					displayName: config.variable,
					variable: config.variable,
					groupId: 0,
					groupDisplayName: ""
				})
			}
		})

		setSelectedOptions(savedOptions)
		setFieldConfigs(configsByFieldId)
	}

	const handleSelectionChange = (options: CustomFieldOption[] | null) => {
		const updatedOptions = options ?? []

		setSelectedOptions(updatedOptions)

		const updatedConfigs: Record<number, FieldConfig> = {}

		updatedOptions.forEach(option => {
			const existingConfig = fieldConfigs[option.id]
			updatedConfigs[option.id] = existingConfig ?? {
				customFieldId: option.id,
				variable: option.variable,
				instruction: "",
				conflictRule: "overwrite"
			}
		})

		setFieldConfigs(updatedConfigs)

		const fields = buildFields(updatedOptions, updatedConfigs)
		contentEditor.changeContent({ customFieldFillData: { fields } })
	}

	const handleInstructionChange = (customFieldId: number, instruction: string) => {
		const updatedConfigs: Record<number, FieldConfig> = {
			...fieldConfigs,
			[customFieldId]: {
				...fieldConfigs[customFieldId],
				instruction
			}
		}

		setFieldConfigs(updatedConfigs)

		const fields = buildFields(selectedOptions, updatedConfigs)
		contentEditor.changeContent({ customFieldFillData: { fields } })
	}

	const handleConflictRuleChange = (customFieldId: number, conflictRule: "overwrite" | "keep") => {
		const updatedConfigs: Record<number, FieldConfig> = {
			...fieldConfigs,
			[customFieldId]: {
				...fieldConfigs[customFieldId],
				conflictRule
			}
		}

		setFieldConfigs(updatedConfigs)

		const fields = buildFields(selectedOptions, updatedConfigs)
		contentEditor.changeContent({ customFieldFillData: { fields } })
	}

	const handleFieldRemove = (customFieldId: number) => {
		handleSelectionChange(selectedOptions.filter(option => option.id !== customFieldId))
	}

	const handleSave = () => {
		onSave(contentEditor.content)
	}

	return (
		<BlockBodySectionEditor
			title="ADICIONAR CAMPO PERSONALIZADO COM IA"
			onSave={handleSave}
			onOpen={handleOpen}
			AsideDrawerProps={{
				width: DRAWER_WIDTH,
				children: (
					<Grid
						container
						spacing={2}
					>
						<Grid
							item
							xs={12}
						>
							<CustomFieldMultiSelect
								value={selectedOptions}
								onChange={handleSelectionChange}
								options={allOptions}
							/>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid
								container
								direction="column"
								spacing={2}
							>
								{selectedOptions.map(option => {
									const config = fieldConfigs[option.id]

									return (
										<Grid
											item
											key={option.id}
										>
											<CustomFieldCard
												fieldName={option.displayName}
												instruction={config?.instruction ?? ""}
												conflictRule={config?.conflictRule ?? "overwrite"}
												onInstructionChange={instruction => handleInstructionChange(option.id, instruction)}
												onConflictRuleChange={conflictRule => handleConflictRuleChange(option.id, conflictRule)}
												onRemove={() => handleFieldRemove(option.id)}
											/>
										</Grid>
									)
								})}
							</Grid>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid className={classes.aiNote}>
								<Grid className={classes.aiNoteIconBox}>
									<SparklesIcon className={classes.aiNoteIcon} />
								</Grid>

								<Typography className={classes.aiNoteText}>
									{AI_NOTE_PARAGRAPH}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default CustomFieldSelectionEditor
